import { AUDIO_RHYTHM_COLORS, type ColorStop } from './constants'
import type { AudioRhythmColorId, Rgb } from './types'

function clampByte(v: number): number {
  return Math.max(0, Math.min(255, Math.round(v)))
}

export function lerpRgb(a: Rgb, b: Rgb, t: number): Rgb {
  const u = Math.max(0, Math.min(1, t))
  return {
    r: clampByte(a.r + (b.r - a.r) * u),
    g: clampByte(a.g + (b.g - a.g) * u),
    b: clampByte(a.b + (b.b - a.b) * u),
  }
}

export function scaleRgb(color: Rgb, intensity: number): Rgb {
  const k = Math.max(0, Math.min(1, intensity))
  return {
    r: clampByte(color.r * k),
    g: clampByte(color.g * k),
    b: clampByte(color.b * k),
  }
}

export function sampleStops(stops: ColorStop[], t: number): Rgb {
  const u = Math.max(0, Math.min(1, t))
  if (stops.length === 0) return { r: 0, g: 0, b: 0 }
  if (stops.length === 1) return stops[0]!.color
  for (let i = 0; i < stops.length - 1; i++) {
    const a = stops[i]!
    const b = stops[i + 1]!
    if (u >= a.t && u <= b.t) {
      const local = (u - a.t) / Math.max(1e-6, b.t - a.t)
      return lerpRgb(a.color, b.color, local)
    }
  }
  return stops[stops.length - 1]!.color
}

export function createColorSampler(colorId: AudioRhythmColorId): (t: number) => Rgb {
  const preset = AUDIO_RHYTHM_COLORS.find((c) => c.id === colorId) ?? AUDIO_RHYTHM_COLORS[0]!
  return (t: number) => sampleStops(preset.stops, t)
}

export function rgbToCss(color: Rgb, alpha = 1): string {
  return `rgba(${color.r}, ${color.g}, ${color.b}, ${alpha})`
}

export function gradientCss(stops: ColorStop[]): string {
  const parts = stops.map((s) => `rgb(${s.color.r}, ${s.color.g}, ${s.color.b}) ${s.t * 100}%`)
  return `linear-gradient(135deg, ${parts.join(', ')})`
}

/** 拖尾：prev * decay + curr * (1-decay)；smoothing 越大 decay 越大。 */
export function blendTrail(prev: Rgb | undefined, curr: Rgb, smoothing: number): Rgb {
  if (!prev) return curr
  const decay = Math.max(0, Math.min(0.95, smoothing / 100))
  return {
    r: clampByte(prev.r * decay + curr.r * (1 - decay)),
    g: clampByte(prev.g * decay + curr.g * (1 - decay)),
    b: clampByte(prev.b * decay + curr.b * (1 - decay)),
  }
}

export function applyBrightness(frame: Map<number, Rgb>, brightness: number): Map<number, Rgb> {
  const k = Math.max(0, Math.min(2, brightness / 100))
  const out = new Map<number, Rgb>()
  for (const [idx, c] of frame) {
    out.set(idx, {
      r: clampByte(c.r * k),
      g: clampByte(c.g * k),
      b: clampByte(c.b * k),
    })
  }
  return out
}
